import { ControllerProps } from "csi/middleware/common";
import { ModalDialogProps } from "designSystem/components/dialog/ModalDialog";
import PopupDialog from "designSystem/components/dialog/PopupDialog";
import Select from "designSystem/components/select/Select";
import TextField from "designSystem/components/textfield/TextField";
import Checkbox from "designSystem/components/checkbox/Checkbox";
import { red } from "designSystem/styles/color";
import {
	Box,
	Button,
	FlexColumn,
	GridRow,
	OverflowText,
	Text,
	FlexRowCenter,
} from "designSystem/index";

import { ButtonEnum } from "designSystem/types/button-types";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { CopyFoodStoreContext } from "./CopyFoodStore";
import ProductHierarchySelect from "../../SiteAdmin/ProductHierarchy/ProductHierarchySelect";
import { useLocalReactionStore } from "designSystem/hooks";
import { useLocale } from "designSystem/locale";
import { createCopyFoodStore } from "./CopyFoodService";
import { styled } from "@mui/material/styles";

const textPadding: string = "15px";
const textWidth: string = "40%";

const StyledText = styled(Text)((
	{
		theme,
	},
) => ({
	textAlign: "right",
	padding: textPadding,
	width: textWidth,
	fontWeight: "bold",
}));

const StyledFlexRow = styled(FlexRowCenter)((
	{
		theme,
	},
) => ({
	paddingInline: "5%",
}));

export interface CopyFoodFormProps extends ModalDialogProps, ControllerProps {

}

const CopyFoodForm: FunctionComponent<CopyFoodFormProps> = observer((props) => {
	const { actionController, children, onClose, onSave, ...otherProps } = props;
	const store = useLocalReactionStore(() => createCopyFoodStore(actionController, locale, onSave));
	const locale = useLocale();

	const {
		enableCreateProductinProject,
		executeCopyFoodOperation,
		executeIncrementalCopyFoodOperation,
		copyNumber,
		setCopyNumber,
		copyNumberError,
		copyNumberHelperText,
		copySKUs,
		setCopySKUs,
		formulatedProductType,
		foodName,
		setFoodName,
		loading,
		setLeafFolder,
		leafFolderError,
		multiCopyLimit,
		project,
		setProject,
		projectItems,
	} = store || {};

	return (
		store && (
			<CopyFoodStoreContext.Provider value={store}>
				<PopupDialog
					onClose={onClose}
					showClose
					{...otherProps}
				>
					{{
						main: (
							<Box width={550}>
								<FlexColumn>
									<StyledFlexRow>
										<FlexRowCenter
											justifyContent="flex-end"
											padding={textPadding}
											width={textWidth}
										>
											<Text bold res="Food.Attr.Subtype" />
											<Text
												bold
												color={red[400]}
												text="*"
											/>
										</FlexRowCenter>
										<TextField
											disabled
											fullWidth
											value={formulatedProductType}
										/>
									</StyledFlexRow>
									<StyledFlexRow>
										<StyledText res="Food.Name" />
										<TextField
											fullWidth
											onChange={(e) => setFoodName(e.target.value)}
											value={foodName}
										/>
									</StyledFlexRow>
									{enableCreateProductinProject && (
										<StyledFlexRow>
											<StyledText res="Project.Name" />
											<Select
												fullWidth
												items={projectItems}
												onChange={(e) => setProject(e.target.value)}
												value={project}
											/>
										</StyledFlexRow>
									)}
									{multiCopyLimit > 1 && (
										<StyledFlexRow>
											<StyledText res="CopyNumber" />
											<TextField
												error={copyNumberError}
												fullWidth
												helperText={copyNumberHelperText}
												onChange={(e) => setCopyNumber(parseInt(e.target.value))}
												type="number"
												value={copyNumber}
											/>
										</StyledFlexRow>
									)}
									<StyledFlexRow>
										<StyledText res="CopySKUs" />
										<Box width="100%">
											<Checkbox
												checked={copySKUs}
												onChange={(e, value) => setCopySKUs(value)}
											/>
										</Box>
									</StyledFlexRow>
									<ProductHierarchySelect
										actionController={actionController}
										error={leafFolderError}
										onChange={setLeafFolder}
									/>
								</FlexColumn>
							</Box>
						),
						buttons: (
							<GridRow
								alignItems="center"
								flexWrap="nowrap"
								growChild={0}
								spacing={2}
								textAlign="left"
							>
								<OverflowText />
								<Button
									design="secondary"
									disabled={loading}
									id={ButtonEnum.BUTTON_SAVE_COPY}
									onClick={executeIncrementalCopyFoodOperation}
									res={ButtonEnum.BUTTON_SAVE_COPY}
								/>
								<Button
									design="primary"
									disabled={loading}
									id={ButtonEnum.BUTTON_SAVE}
									onClick={executeCopyFoodOperation}
									res={ButtonEnum.BUTTON_SAVE}
								/>
								<Button
									design="link"
									onClick={onClose}
									res={ButtonEnum.BUTTON_CANCEL}
								/>
							</GridRow>
						),
					}}
				</PopupDialog>
			</CopyFoodStoreContext.Provider>
		)
	);
});

export default CopyFoodForm;